import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Scissors, Ruler, Clock, Award } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HomePage = () => {
  const [services, setServices] = useState([]);
  const [collections, setCollections] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, collectionsRes] = await Promise.all([
          axios.get(`${API}/services`),
          axios.get(`${API}/collections`)
        ]);
        setServices(servicesRes.data);
        setCollections(collectionsRes.data.slice(0, 4));
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 40 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
  };

  const features = [
    { icon: Scissors, label: "Master Craftsmanship", desc: "Hand-finished details and traditional tailoring techniques" },
    { icon: Ruler, label: "Custom Fit", desc: "Measured and refined through a personalized fitting process" },
    { icon: Clock, label: "Timeless Design", desc: "Classic silhouettes with modern proportion" },
    { icon: Award, label: "Premium Fabrics", desc: "Curated mills from Italy and England" }
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-section relative flex items-center justify-center">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_custom-threads-65/artifacts/x4v2ovyo_DSCF0795.jpeg')`
          }}
        />
        {/* Brightened overlay for better mobile visibility */}
        <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/50 to-background/70 md:from-background/30 md:via-background/40 md:to-background/60" />
        
        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 text-center">
          <motion.h1 
            className="font-serif text-4xl sm:text-5xl lg:text-7xl tracking-tight mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            data-testid="hero-title"
          >
            Custom Tailoring for the<br />
            <span className="italic text-gold-gradient">Modern-Day Renaissance</span> Man
          </motion.h1>

          <motion.p 
            className="text-lg md:text-xl text-foreground/80 max-w-2xl mx-auto mb-4 font-light"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            data-testid="hero-description"
          >
            Custom suiting and shirting, handcrafted to your exact measurements.
          </motion.p>
          
          <motion.p 
            className="text-base text-foreground/70 mb-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            Suits from $995 · Shirts from $195
          </motion.p>

          <motion.div 
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <Link
              to="/booking"
              data-testid="hero-book-btn"
              className="luxury-btn bg-primary text-primary-foreground px-12 py-4 text-xs uppercase tracking-[0.2em] hover:bg-foreground transition-all duration-300"
            >
              Book Consultation
            </Link>
            <Link
              to="/collections"
              data-testid="hero-collections-btn"
              className="text-sm text-foreground/80 hover:text-primary transition-colors flex items-center gap-2 mt-2 sm:mt-0"
            >
              View Collections <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-12 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          <div className="w-px h-16 bg-gradient-to-b from-transparent via-primary to-transparent" />
        </motion.div>
      </section>

      {/* Features Bar */}
      <section className="border-y border-white/5 bg-card/50">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4">
            {features.map((feature, index) => (
              <div 
                key={feature.label}
                data-testid={`feature-${index}`}
                className={`py-8 md:py-12 flex flex-col items-center text-center ${
                  index < 3 ? "border-r border-white/5" : ""
                }`}
              >
                <feature.icon strokeWidth={1} size={32} className="text-primary mb-4" />
                <h3 className="text-sm font-medium mb-1">{feature.label}</h3>
                <p className="text-xs text-muted-foreground">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Statement */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <motion.div 
            className="max-w-4xl mx-auto text-center"
            {...fadeInUp}
            viewport={{ once: true }}
            whileInView="animate"
            initial="initial"
          >
            <p className="mono-text text-primary mb-6">Our Philosophy</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-8" data-testid="philosophy-title">
              True Luxury Lives in<br />
              <span className="italic">the Details</span>
            </h2>
            <div className="space-y-4 text-foreground/80 text-lg font-light">
              <p>Each Laguerre garment is a testament to precision, heritage, and restraint —</p>
              <p>combining time-honored tailoring with modern sensibility.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-24 bg-card/30">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-16 gap-6">
            <div>
              <p className="mono-text text-primary mb-4">What We Offer</p>
              <h2 className="font-serif text-3xl sm:text-4xl tracking-tight" data-testid="services-title">
                Our Services
              </h2>
            </div>
            <Link
              to="/booking"
              data-testid="services-view-all"
              className="text-sm text-primary flex items-center gap-2 hover:gap-4 transition-all"
            >
              View All Services <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                data-testid={`service-card-${index}`}
                className="service-card bg-card/50 border border-white/5 p-8 gold-glow flex flex-col"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <h3 className="font-serif text-xl mb-3">{service.name}</h3>
                <p className="text-sm text-muted-foreground mb-6 line-clamp-3 flex-grow">
                  {service.description}
                </p>
                <div className="flex justify-between items-end">
                  <div>
                    <p className="mono-text text-primary text-[10px]">{service.duration}</p>
                    <p className="text-sm font-light mt-1">{service.price}</p>
                  </div>
                </div>
                <Link 
                  to="/booking" 
                  className="mt-4 text-xs text-primary hover:text-foreground transition-colors flex items-center gap-1"
                >
                  Book a fitting <ArrowRight size={12} strokeWidth={1.5} />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Collections */}
      <section className="py-24">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-16 gap-6">
            <div>
              <p className="mono-text text-primary mb-4">Portfolio</p>
              <h2 className="font-serif text-3xl sm:text-4xl tracking-tight" data-testid="collections-title">
                Featured Collections
              </h2>
            </div>
            <Link
              to="/collections"
              data-testid="collections-view-all"
              className="text-sm text-primary flex items-center gap-2 hover:gap-4 transition-all"
            >
              View All <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {collections.map((item, index) => (
              <motion.div
                key={item.id}
                data-testid={`collection-item-${index}`}
                className="collection-item group cursor-pointer"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <img
                  src={item.image_url}
                  alt={item.title}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="collection-item-overlay" />
                <div className="absolute inset-0 p-6 flex flex-col justify-end">
                  <p className="mono-text text-primary text-[10px] mb-2">{item.category}</p>
                  <h3 className="font-serif text-xl group-hover:text-primary transition-colors">
                    {item.title}
                  </h3>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_custom-threads-65/artifacts/l9ejqc49_DSCF0789.jpg')`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/90 to-background" />
        
        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="mono-text text-primary mb-6">Begin Your Journey</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-8" data-testid="cta-title">
              Your Perfect Garment<br />
              <span className="italic">Awaits</span>
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto mb-12 font-light">
              Schedule a private consultation with laguerre.co and begin 
              your journey towards sartorial excellence.
            </p>
            <Link
              to="/booking"
              data-testid="cta-book-btn"
              className="luxury-btn inline-block bg-primary text-primary-foreground px-12 py-5 text-xs uppercase tracking-[0.2em] hover:bg-foreground transition-all duration-300"
            >
              Schedule Your Appointment
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
